import { createRegistry } from "./registry";

/** A keyboard shortcut; `keys` is such as "mod+k" (Ctrl, or Cmd on a Mac) or "?". */
export interface Shortcut {
  id: string;
  keys: string;
  /** i18n key of what it does, for the shortcuts dialog. */
  labelKey: string;
  run(): void;
}

interface Chord {
  key: string;
  mod: boolean;
  alt: boolean;
  shift: boolean | undefined;
}

/** Parses "mod+shift+k"; shift is only checked when written, so "?" works on any layout. */
export function parseKeys(keys: string): Chord {
  const parts = keys.toLowerCase().split("+");
  const key = parts.pop() ?? "";
  return {
    key,
    mod: parts.includes("mod"),
    alt: parts.includes("alt"),
    shift: parts.includes("shift") ? true : undefined,
  };
}

export function matches(event: KeyboardEvent, keys: string): boolean {
  const chord = parseKeys(keys);
  return (
    event.key.toLowerCase() === chord.key &&
    (event.ctrlKey || event.metaKey) === chord.mod &&
    event.altKey === chord.alt &&
    (chord.shift === undefined || event.shiftKey === chord.shift)
  );
}

/** Whether the user is typing: plain-key shortcuts must not fire there. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Runs the first matching shortcut; in a text field only chords with a modifier fire. */
export function handleKeydown(event: KeyboardEvent, shortcuts: Shortcut[]): void {
  if (event.defaultPrevented) return;
  const typing = isTyping(event.target);
  const shortcut = shortcuts.find(
    (s) => matches(event, s.keys) && !(typing && !parseKeys(s.keys).mod),
  );
  if (!shortcut) return;
  event.preventDefault();
  shortcut.run();
}

/** Whether keys read the Mac way (⌘ rather than Ctrl). */
export const isMac = (): boolean => /mac|iphone|ipad/i.test(navigator.userAgent);

/** How the keys read on screen: "Ctrl K", or "⌘ K" on a Mac. */
export function formatKeys(keys: string, mac: boolean): string {
  const names: Record<string, string> = {
    mod: mac ? "⌘" : "Ctrl",
    shift: "Shift",
    alt: mac ? "⌥" : "Alt",
  };
  return keys
    .split("+")
    .map((part) => names[part] ?? part.toUpperCase())
    .join(" ");
}

export function createShortcutRegistry() {
  const registry = createRegistry<Shortcut>("shortcuts");
  return { register: registry.register, shortcuts: registry.list };
}

const registry = createShortcutRegistry();

export const registerShortcut = (shortcut: Shortcut): void => registry.register(shortcut);
export const shortcuts = (): Shortcut[] => registry.shortcuts();

/** Installs the one keydown listener of the app; returns its removal. */
export function installShortcuts(target: Window, list: () => Shortcut[] = shortcuts): () => void {
  const listener = (event: KeyboardEvent) => handleKeydown(event, list());
  target.addEventListener("keydown", listener);
  return () => target.removeEventListener("keydown", listener);
}
